import { useState } from "react";
import { Group, Text, TextInput } from "@mantine/core";
import { useDraggable, useDroppable } from "@dnd-kit/core";

import NameEdit from "../../NameEdit/NameEdit";
import Formula from "../../Formula/Formula";
import MoreActions from "./MoreActions";
import FieldFunctionWrapper from "./FieldFunctionWrapper";
import {
  DRAG_CONDITION_FIELD,
  DROP_CONDITION_LEFT_FIELD,
  conditionLeftFieldId,
} from "../../../dnd/kinds";
import { useSelectIsFieldHovered } from "../../../../../redux/features/queryBuilder/queryBuilderSlice";
import classes from "./Field.module.scss";

/**
 * Left side of a condition row. Handles FIELD / FORMULA types — VALUE /
 * DATE / BOOL only live on the right side.
 *
 * Drop behavior: when an entity-field is dropped here, the central handler
 * fires updateField with the new leftFieldId + leftDisplayedEntityId. The
 * component itself only registers the droppable and visualizes hover state.
 */
export default function LeftField({
  ruleId,
  queryId,
  groupId,
  forJoin,
  fieldsData,
  canEdit = true,
}) {
  const [nameEditMode, setNameEditMode] = useState(false);
  const fieldData = fieldsData?.leftPart;
  const entityData = fieldData?.entityField;
  const savedFunctions = entityData?.entityFieldFunction;
  const fieldType = fieldData?.type;

  const dropId = conditionLeftFieldId({
    queryId,
    groupId,
    fieldId: fieldsData?.id,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: dropId,
    data: {
      kind: DROP_CONDITION_LEFT_FIELD,
      fieldsData,
      queryId,
      groupId,
      forJoin,
    },
  });

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } =
    useDraggable({
      id: `condition-left-field-drag::${fieldsData?.id}`,
      data: {
        kind: DRAG_CONDITION_FIELD,
        entityField: entityData,
        fieldsData,
        side: "left",
        queryId,
        groupId,
        forJoin,
      },
      disabled: !canEdit || fieldType !== "FIELD" || !entityData,
    });

  const setNodeRef = (node) => {
    setDropRef(node);
    setDragRef(node);
  };

  const sliceHovered = useSelectIsFieldHovered({
    queryId,
    conditionGroupId: groupId,
    fieldId: fieldsData?.id,
    side: "left",
  });
  const showDropHint = isOver || sliceHovered;

  const dragHandleProps =
    fieldType === "FIELD" && canEdit && entityData
      ? { ...attributes, ...listeners }
      : {};

  const dropField = (
    <DropFieldVisual
      setNodeRef={setNodeRef}
      hovered={showDropHint}
      value={entityData?.name || entityData?.field?.name}
      canEdit={canEdit}
      dragHandleProps={dragHandleProps}
      isDragging={isDragging}
    />
  );

  const nameEdit = (
    <NameEdit
      ruleId={ruleId}
      id={entityData?.id}
      defaultValue={entityData?.name || entityData?.field?.name}
      onDone={() => setNameEditMode(false)}
    />
  );

  const functionWrapper = (
    <FieldFunctionWrapper
      ruleId={ruleId}
      fieldsData={fieldsData}
      functionData={savedFunctions}
      content={dropField}
      setNameEditMode={setNameEditMode}
      id={entityData?.id}
      canEdit={canEdit}
    />
  );

  let element = null;
  if (nameEditMode) {
    element = nameEdit;
  } else if (fieldType === "FIELD") {
    element = savedFunctions ? functionWrapper : dropField;
  } else if (fieldType === "FORMULA") {
    element = (
      <Formula
        ruleId={ruleId}
        fieldsData={fieldsData}
        left={true}
        canEdit={canEdit}
        leadingSlot={savedFunctions ? functionWrapper : dropField}
      />
    );
  }

  return (
    <Group gap={6} align="center" wrap="nowrap" className={classes.fieldWrap}>
      {element}
      {canEdit && (
        <MoreActions
          ruleId={ruleId}
          fieldsData={fieldsData}
          id={entityData?.id}
          setNameEditMode={setNameEditMode}
          showFunctionsPopover={!savedFunctions}
          fieldType={fieldType}
          showChangeAlias
          allowValueEntry={false}
          isRightField={false}
        />
      )}
    </Group>
  );
}

/** Hovered → "Drop entity here" target. Otherwise a disabled label. */
function DropFieldVisual({
  setNodeRef,
  hovered,
  value,
  canEdit,
  dragHandleProps = {},
  isDragging = false,
}) {
  if (hovered) {
    return (
      <div ref={setNodeRef} className={classes.dropTargetActive}>
        <Text size="sm" c="dimmed" ta="center">
          {canEdit ? "Drop entity here" : ""}
        </Text>
      </div>
    );
  }
  return (
    <div
      ref={setNodeRef}
      className={classes.fieldHolder}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      {...dragHandleProps}
    >
      <TextInput
        value={value ?? ""}
        disabled
        variant="filled"
        readOnly
        styles={{
          input: {
            cursor: dragHandleProps.onPointerDown
              ? "grab"
              : canEdit
                ? "copy"
                : "default",
          },
        }}
      />
    </div>
  );
}
